import { IMCenter, IMCenterStage, IMLesson, IMUnit } from '../types'
import { createNodeList } from '../../nodeCreationHelpers'
import { firstAndLast, snakeKabobToTitleCase } from '../../../utility'
import { parseStandardsGroup } from './standards'
import {
  IDocumentNode,
  INodeList,
  ISimplifiedNode,
} from '../../../components/ingestion/IngestedTree'
import { requiredPreparation } from './ancillary_content'
import { createUnorderedList, wrapTag } from '../../html_utility'
import { isUnit } from '../utility'
import { IM360Parser } from './IM360Parser'
import { createDocumentNode } from './createNodeWrappers'

export function createCentersNode({ centers }: { centers: IMCenter[] }) {
  if (!centers.length) return
  return createNodeList<IDocumentNode>({ title: 'Centers' }, [
    createDocumentNode<INodeList>('Centers', [
      createNodeList({ title: 'Centers' }, parseCenters(centers)),
    ]),
  ])
}

function parseCenters(centers: IMCenter[]) {
  return centers.map(parseCenter)
}

function parseCenter(center: IMCenter) {
  const grades = center.grades?.length && firstAndLast(center.grades).join('-')
  const gradePart = grades ? ` (${grades})` : ''
  return createDocumentNode(
    `${center.title}${gradePart}`,
    centerChildren(center),
  )
}

function centerChildren(center: IMCenter) {
  return [
    IM360Parser.getInstance().createNoteNode({
      title: 'Narrative',
      value: center.narrative,
      studentFacing: false,
    }),
    createNodeList({ title: 'Stages' }, center.stages?.map(parseStage) ?? []),
  ]
}

function parseStage(stage: IMCenterStage) {
  return createDocumentNode(stage.title, [
    ...requiredPreparation(stage.ancillary_content),
    ...parseStandardsGroup(stage.standards, stage.ancillary_content),
    ...childNotes(stage),
  ])
}

function childNotes(stage: IMCenterStage) {
  const childKeys: (keyof IMCenterStage)[] = [
    'narrative',
    'teacher_instructions',
  ]
  return childKeys.reduce<ISimplifiedNode[]>((acc, key) => {
    if (stage[key]) {
      return [
        ...acc,
        IM360Parser.getInstance().createNoteNode({
          title: snakeKabobToTitleCase(key),
          value: stage[key] as string,
          studentFacing: false,
        }),
      ]
    }
    return acc
  }, [])
}

function lessonCentersList(lesson: IMLesson) {
  const centers = lesson.references?.reduce<string[]>((acc, reference) => {
    if (reference.type !== 'Stage') {
      return acc
    }
    return [...acc, `${reference.ancestors.center!.title}, ${reference.title}`]
  }, [])
  if (!centers?.length) return ''
  return wrapTag('span', lesson.ordinal_title) + createUnorderedList(centers)
}

export function centersList(unit: IMUnit | IMLesson) {
  if (!isUnit(unit)) {
    return lessonCentersList(unit)
  }
  return unit.sections.reduce<string>((unitAcc, section) => {
    return section.lessons.reduce((lessonAcc, lesson) => {
      return lessonAcc + lessonCentersList(lesson)
    }, unitAcc)
  }, '')
}

export function suggestedCenters(unit: IMUnit | IMLesson) {
  const list = centersList(unit)
  if (!list) return []
  return [
    createNodeList({ title: 'Suggested Centers' }, [
      createDocumentNode('Suggested Centers', [
        IM360Parser.getInstance().createNoteNode({
          title: 'Suggested Centers',
          studentFacing: false,
          value: list,
        }),
      ]),
    ]),
  ]
}
